import { AnnouncementType } from '@zennolab_platform/data-access-notification-service-api';
import { useTheme } from '@zennolab_platform/webui-kit/dist/ui';
import { useFormatMessage } from '@zennolab_platform/webui-sdk/dist/hooks';

import { useAnnouncementLabels } from '../../hooks/useAnnouncementLabels';
import { KitTag } from './styled';

/** Outlined tag colours: text in the tag colour, light background and border. */
export type TagColors = { color: string; backgroundColor: string; borderColor: string };

/** 10% background and 50% border of a hex colour, as the kit's S10/S50 steps, for colours the theme has no steps for. */
const tint = (hex: string): TagColors => ({ color: hex, backgroundColor: `${hex}1A`, borderColor: `${hex}80` });

/** Type glyphs of the ZennoBrowser notification card (webui-sdk), 16px grid. */
const GLYPHS: Partial<Record<AnnouncementType, string>> = {
  [AnnouncementType.ANNOUNCEMENT_TYPE_UPDATE]: 'M8 2v8m0 0-3-3m3 3 3-3M2.5 12.5v1h11v-1',
  [AnnouncementType.ANNOUNCEMENT_TYPE_PROMO]:
    'M2 6h12v8H2zM1.5 3.5h13V6h-13zM8 3.5V14M8 3.5C6 1 4 2 5 3.5M8 3.5c2-2.5 4-1.5 3 0',
  [AnnouncementType.ANNOUNCEMENT_TYPE_MAINTENANCE]: 'M8 1.5 15 14H1L8 1.5Zm0 5v3.5m0 2v.2',
  [AnnouncementType.ANNOUNCEMENT_TYPE_NEWS]:
    'M2 3h9v10H3a1 1 0 0 1-1-1V3Zm9 3h3v6a1 1 0 0 1-2 0V6M4.5 5.5h4m-4 2.5h4m-4 2.5h2.5',
};

/** «Важное»: the item also pops a toast in ZennoBrowser; warning colour as on the ZennoBrowser card. */
export function ImportantTag() {
  const { t } = useFormatMessage();
  const { color } = useTheme();
  return (
    <KitTag
      data-testid="ImportantTag"
      style={{ color: color.OrangeS100, backgroundColor: color.OrangeS10, borderColor: color.OrangeS50 }}
    >
      {t({
        id: 'announcements.list.important',
        defaultMessage: 'Важное',
        description: 'Метка «Важное» анонса в списке',
      })}
    </KitTag>
  );
}

/** The announcement type with the icon and colour it has in ZennoBrowser. */
export function TypeTag({ type }: { type: AnnouncementType }) {
  const { typeLabel } = useAnnouncementLabels();
  const { color } = useTheme();
  const colors: Partial<Record<AnnouncementType, TagColors>> = {
    [AnnouncementType.ANNOUNCEMENT_TYPE_UPDATE]: {
      color: color.Blue100,
      backgroundColor: color.BlueS10,
      borderColor: color.BlueS50,
    },
    [AnnouncementType.ANNOUNCEMENT_TYPE_PROMO]: {
      color: color.GreenS100,
      backgroundColor: color.GreenS10,
      borderColor: color.GreenS50,
    },
    [AnnouncementType.ANNOUNCEMENT_TYPE_MAINTENANCE]: {
      color: color.OrangeS100,
      backgroundColor: color.OrangeS10,
      borderColor: color.OrangeS50,
    },
    [AnnouncementType.ANNOUNCEMENT_TYPE_NEWS]: {
      color: color.PurpleS100,
      backgroundColor: color.PurpleS10,
      borderColor: color.PurpleS50,
    },
  };
  const style = colors[type] ?? {
    color: color.Neutral70,
    backgroundColor: color.Neutral10,
    borderColor: color.Neutral40,
  };
  const glyph = GLYPHS[type];
  return (
    <KitTag data-testid={`TypeTag-${type}`} style={style}>
      {glyph && (
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d={glyph} stroke={style.color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
      {typeLabel(type)}
    </KitTag>
  );
}

/** Tariff colours of the ZennoBrowser page in UA2; Start, personal and unknown codes are neutral. */
const TARIFF_COLORS: Record<string, string> = {
  Cloud: '#1991FF',
  Pro: '#1CA932',
  Business: '#9155FF',
};
const NEUTRAL_TARIFF = '#505578';

const tariffColor = (code: string) => TARIFF_COLORS[code] ?? NEUTRAL_TARIFF;

export const tariffTagColors = (code: string): TagColors => tint(tariffColor(code));

export function TariffTag({ code }: { code: string }) {
  return (
    <KitTag data-testid={`TariffTag-${code}`} style={tariffTagColors(code)}>
      {code}
    </KitTag>
  );
}
